// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Button, Fade, CircularProgress, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { dollars } from 'Utils/numberFormat';
import { RootState } from 'Store';
import { GetCommittedTiersThunk, GetOrgThunk, RevertRateThunk, UpdateRateThunk } from 'Features/admin/adminThunk';
import HistoryList from 'Components/HistoryList';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

// the Select's choice for a figure that is not a ladder tier's price
const NEGOTIATED = 'negotiated';

interface EditRateProps {
  // whether the account's rate has a default to go back to (the plan band, the pay-as-you-go ladder)
  revertLabel: string | null;
}

/**
 * One Edit Rate for every account kind (one rate per account, 2026-10-29): pick a Credit Ladder tier, which fixes the
 * figure at the tier's price, or Negotiated and type the figure; a note is required. Revert, where the account has a
 * default, puts a plan back on its band or pay-as-you-go back on the ladder. The server writes the rate where the
 * account keeps it and answers the resolved rate; the org is reloaded so every line on the tab follows. The change log
 * under the controls is the one the server serves beside the rate.
 */
export const EditRate = ({ revertLabel }: EditRateProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org, committedTiers } = useAppSelector((state: RootState) => state.admin);
  const history = org?.accountRate?.history || [];

  const [editMode, setEditMode] = useState<'set' | 'revert' | null>(null);
  const [choice, setChoice] = useState<string>(NEGOTIATED);
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // the ladder's tiers are the menu; fetched once, when the editor first opens with none loaded
  useEffect(() => {
    if (editMode === 'set' && (!committedTiers || committedTiers.length === 0)) {
      dispatch(GetCommittedTiersThunk());
    }
  }, [editMode]);

  const tiers = (committedTiers || []).filter((tier) => typeof tier.dollarPerCredit === 'number');
  const pickedTier = tiers.find((tier) => tier.name === choice) || null;
  const figure = pickedTier ? pickedTier.dollarPerCredit : parseFloat(rate);
  const canSave = !!notes.trim() && (editMode === 'revert' || (typeof figure === 'number' && figure > 0)) && !saving;

  const cancelEdit = () => {
    setEditMode(null);
    setChoice(NEGOTIATED);
    setRate('');
    setNotes('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const result = editMode === 'revert'
      ? await dispatch(RevertRateThunk(notes.trim()))
      : await dispatch(UpdateRateThunk({ dollarPerCredit: figure as number, ladderTier: pickedTier ? pickedTier.name : null, notes: notes.trim() }));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      cancelEdit();
    } else {
      setError(result.message || 'Failed to update the rate');
    }
    setSaving(false);
  };

  const tierLabel = (tier: { name: string; dollarPerCredit?: number }) => `${tier.name === 'Standard' ? 'Entry' : tier.name} — ${dollars(tier.dollarPerCredit)} per credit`;

  return (
    <Box sx={styles.root}>
      {!editMode && (
        <Box sx={styles.buttonRow}>
          <Button disabled={!user?.pricingAccess} sx={styles.button} onClick={() => setEditMode('set')} color="primary" variant="outlined">
            Edit Rate
          </Button>
          {revertLabel && (
            <Button disabled={!user?.pricingAccess} sx={styles.button} onClick={() => setEditMode('revert')} variant="text">
              {revertLabel}
            </Button>
          )}
        </Box>
      )}
      {editMode && (
        <Box sx={styles.form}>
          {editMode === 'set' ? (
            <Box sx={styles.fieldRow}>
              <FormControl variant="standard" sx={styles.tierSelect}>
                <InputLabel id="rateChoiceLabel">New rate</InputLabel>
                <Select labelId="rateChoiceLabel" id="rateChoice" value={choice} onChange={(e) => setChoice(String(e.target.value))}>
                  {tiers.map((tier) => (
                    <MenuItem value={tier.name} key={tier._id}>{tierLabel(tier)}</MenuItem>
                  ))}
                  <MenuItem value={NEGOTIATED}>Negotiated — type the figure</MenuItem>
                </Select>
              </FormControl>
              <TextField
                id="rateFigure"
                variant="standard"
                type="number"
                label="Per credit"
                disabled={!!pickedTier}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { 'aria-label': 'Per credit', min: 0, step: 0.01 } }}
                onChange={(e) => setRate(e.target.value)}
                value={pickedTier ? String(pickedTier.dollarPerCredit) : rate}
                sx={styles.rateField}
              />
            </Box>
          ) : (
            <Typography>{revertLabel}</Typography>
          )}
          <TextField
            id="rateNotes"
            label="Notes (required)"
            variant="standard"
            multiline
            minRows={2}
            onChange={(e) => setNotes(e.target.value)}
            value={notes}
            sx={styles.notesField}
          />
          {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
          <Box sx={styles.buttonRow}>
            <Button sx={styles.button} disabled={!canSave} onClick={save} color="primary" variant="contained">
              <Fade in={saving}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!saving}>
                <span>{editMode === 'revert' ? 'Revert Rate' : 'Save Rate'}</span>
              </Fade>
            </Button>
            <Button sx={styles.button} onClick={cancelEdit} variant="text">Cancel</Button>
          </Box>
        </Box>
      )}
      <HistoryList
        entries={history}
        getKey={(entry) => entry._id}
        renderRow={(entry) => (
          <Box sx={styles.historyRow}>
            <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
            <Typography variant="body2" sx={styles.historyChange}>
              {typeof entry.rate === 'number' ? `${dollars(entry.rate)} per credit` : 'Default rate'}
            </Typography>
            <Typography variant="body2">{entry.changedBy}</Typography>
            <Typography variant="body2" sx={styles.historyNotes}>{entry.notes}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default EditRate;
